import HeaderBar from './HederBar';
import { useSearchParams } from 'react-router-dom';
import { memo, useState } from 'react';
import { ReportType } from '@declarations/common/reportType';
import { Box, Button } from '@mui/material';
import { useForm } from 'react-hook-form';
import { serialize } from 'object-to-formdata';
import Field from '@components/CRUDForm/Field';
import { Fields } from '@components/CRUDForm/declarations/FormField';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { yesNoOptions } from '../../routes/const';
import ClinicalLogRecordService from '@api/clinicalLogRecordService';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import Errors from '@constants/locale/errors';

type Props = {
  reportType: ReportType;
  onSave?: () => void;
};

const validationSchema = object({
  legalReviewRecommended: string().required(Errors.REQUIRED_FIELD),
});

function ClinicalReviewForm({ reportType, onSave }: Props) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const fields: Fields<any> = [
    {
      type: 'text',
      required: false,
      name: 'note',
      label: 'Notes',
      props: {
        disabled: loading,
        multiline: true,
        minRows: 3,
        inputProps: { maxLength: 180 },
      },
    },
    {
      type: 'radio',
      required: true,
      name: 'legalReviewRecommended',
      label: 'Would you recommend a full legal review based on your findings?',
      options: yesNoOptions,
      props: {
        disabled: loading,
      },
    },
    {
      fullWidth: true,
      type: 'dropzone',
      name: 'attachments',
      label: 'Upload Completed Review',
      props: {
        disabled: loading,
        maxFiles: 1,
        text: 'Drag and drop a clinical review here, or click to choose it, that not exceeding 50MB in size.'
      },
    },
  ];

  const form = useForm<any>({
    defaultValues: { reportId: searchParams.get('id'), reportType, subject: null },
    shouldFocusError: true,
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema, {}, { mode: 'sync' }),
  });

  const onSubmit = form.handleSubmit(data => {
    setLoading(true);
    const formData = serialize(data, {
      dotsForObjectNotation: false,
      nullsAsUndefineds: true,
    });
    ClinicalLogRecordService.createRecord(formData)
      .then(() => {
        onSave && onSave();
        form.reset();
      })
      .catch(error => {
        if (error instanceof AxiosError) {
          const { response } = error;
          if (response && response.status === 400) {
            if ('message' in response.data) {
              toast.error(response.data.message);
            }
            if ('fields' in response.data) {
              response.data.fields.forEach(({ field }: { field: string; errors: string[] }) => {
                form.setError(field as any, {});
              });
            }
            return;
          }
        }
        toast.error('Unable to save entity');
      })
      .finally(() => setLoading(false));
  });

  const description = (
    <div>
      Download{' '}
      <a
        href={process.env.PUBLIC_URL + '/Expert Clinical Review Template.docx'}
        style={{ color: '#FFF' }}
        download
      >
        Clinical Review Template
      </a>{' '}
      to begin review.
    </div>
  );

  return (
    <>
      <HeaderBar title="Clinical Review" description={description} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', mt: '32px', maxWidth: '600px' }}>
        {fields.map(field => (
          <Field key={field.name} field={field} form={form} />
        ))}
        <Box>
          <Button disabled={loading} variant="contained" onClick={onSubmit}>
            Complete
          </Button>
        </Box>
      </Box>
    </>
  );
}

export default memo(ClinicalReviewForm);
